import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileDown, 
  Loader2, 
  Save, 
  Settings, 
  Palette,
  FileText,
  Building,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PDFSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    organization_name: 'The HELP Center',
    primary_color: '#92298d',
    secondary_color: '#6366f1',
    logo_enabled: true,
    page_orientation: 'auto',
    paper_size: 'letter',
    margins: {
      top: 0.75,
      bottom: 0.75,
      left: 0.75,
      right: 0.75
    },
    font_family: 'Helvetica',
    font_sizes: {
      title: 18,
      subtitle: 14,
      heading: 12,
      body: 10,
      small: 8
    },
    cover_page_enabled: false,
    confidential_watermark: false,
    signature_lines_enabled: false,
    include_page_numbers: true,
    include_generation_timestamp: true,
    include_generated_by: true,
    footer_text: 'Confidential - For Internal Use Only',
    disclaimer_text: '',
    organization_address: '',
    organization_phone: '',
    organization_email: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pdf-settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch PDF settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pdf-settings`, settings);
      toast.success('PDF settings saved successfully');
    } catch (error) {
      console.error('Failed to save PDF settings:', error);
      toast.error('Failed to save PDF settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateMargin = (side, value) => {
    setSettings(prev => ({
      ...prev,
      margins: { ...prev.margins, [side]: parseFloat(value) || 0 }
    }));
  };

  const updateFontSize = (type, value) => {
    setSettings(prev => ({
      ...prev,
      font_sizes: { ...prev.font_sizes, [type]: parseInt(value) || 10 }
    }));
  };

  // Only system admins can access this page
  if (user?.role !== 'system_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-96">
          <CardContent className="py-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Access Restricted</h3>
            <p className="text-muted-foreground mt-2">
              Only System Administrators can access PDF settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pdf-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileDown className="w-8 h-8 text-primary" />
            PDF Report Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure branding and formatting for exported PDF reports
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-pdf-settings-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="branding">
            <Building className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="layout">
            <FileText className="w-4 h-4 mr-2" />
            Layout
          </TabsTrigger>
          <TabsTrigger value="typography">
            <Settings className="w-4 h-4 mr-2" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="options">
            <Palette className="w-4 h-4 mr-2" />
            Options
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Information</CardTitle>
              <CardDescription>
                This information will appear in the header and footer of all PDF reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input
                    id="org-name"
                    value={settings.organization_name}
                    onChange={(e) => updateSettings('organization_name', e.target.value)}
                    placeholder="The HELP Center"
                    data-testid="pdf-org-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-email">Organization Email</Label>
                  <Input
                    id="org-email"
                    type="email"
                    value={settings.organization_email}
                    onChange={(e) => updateSettings('organization_email', e.target.value)}
                    placeholder="info@organization.org"
                    data-testid="pdf-org-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-phone">Organization Phone</Label>
                  <Input
                    id="org-phone"
                    value={settings.organization_phone}
                    onChange={(e) => updateSettings('organization_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="pdf-org-phone"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="org-address">Organization Address</Label>
                  <Textarea
                    id="org-address"
                    value={settings.organization_address}
                    onChange={(e) => updateSettings('organization_address', e.target.value)}
                    placeholder="123 Main Street, Suite 100, Nashville, TN 37201"
                    rows={2}
                    data-testid="pdf-org-address"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
              <CardDescription>
                Define your brand colors for headers, accents, and highlights
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateSettings('primary_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                      data-testid="pdf-primary-color"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => updateSettings('primary_color', e.target.value)}
                      className="flex-1"
                      placeholder="#92298d"
                    />
                    <div 
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: settings.primary_color }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Used for headers, table headers, and emphasis</p>
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => updateSettings('secondary_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                      data-testid="pdf-secondary-color"
                    />
                    <Input
                      value={settings.secondary_color}
                      onChange={(e) => updateSettings('secondary_color', e.target.value)}
                      className="flex-1"
                      placeholder="#6366f1"
                    />
                    <div 
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: settings.secondary_color }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Used for accents and secondary elements</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Layout Tab */}
        <TabsContent value="layout" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Page Setup</CardTitle>
              <CardDescription>
                Configure page size, orientation, and margins
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paper Size</Label>
                  <Select 
                    value={settings.paper_size} 
                    onValueChange={(v) => updateSettings('paper_size', v)}
                  >
                    <SelectTrigger data-testid="pdf-paper-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="letter">Letter (8.5&quot; x 11&quot;)</SelectItem>
                      <SelectItem value="legal">Legal (8.5&quot; x 14&quot;)</SelectItem>
                      <SelectItem value="a4">A4 (210mm x 297mm)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Page Orientation</Label>
                  <Select 
                    value={settings.page_orientation} 
                    onValueChange={(v) => updateSettings('page_orientation', v)}
                  >
                    <SelectTrigger data-testid="pdf-orientation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (based on content)</SelectItem>
                      <SelectItem value="portrait">Portrait</SelectItem>
                      <SelectItem value="landscape">Landscape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Margins (inches)</CardTitle>
              <CardDescription>
                Set page margins for all PDF exports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Top</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="2"
                    value={settings.margins?.top || 0.75}
                    onChange={(e) => updateMargin('top', e.target.value)}
                    data-testid="pdf-margin-top"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bottom</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="2"
                    value={settings.margins?.bottom || 0.75}
                    onChange={(e) => updateMargin('bottom', e.target.value)}
                    data-testid="pdf-margin-bottom"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Left</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="2"
                    value={settings.margins?.left || 0.75}
                    onChange={(e) => updateMargin('left', e.target.value)}
                    data-testid="pdf-margin-left"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Right</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="2"
                    value={settings.margins?.right || 0.75}
                    onChange={(e) => updateMargin('right', e.target.value)}
                    data-testid="pdf-margin-right"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Font Settings</CardTitle>
              <CardDescription>
                Configure fonts and sizes for different text elements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Font Family</Label>
                <Select 
                  value={settings.font_family} 
                  onValueChange={(v) => updateSettings('font_family', v)}
                >
                  <SelectTrigger data-testid="pdf-font-family">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Helvetica">Helvetica (Sans-serif)</SelectItem>
                    <SelectItem value="Times-Roman">Times Roman (Serif)</SelectItem>
                    <SelectItem value="Courier">Courier (Monospace)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    type="number"
                    min="12"
                    max="36"
                    value={settings.font_sizes?.title || 18}
                    onChange={(e) => updateFontSize('title', e.target.value)}
                    data-testid="pdf-font-title"
                  />
                  <p className="text-xs text-muted-foreground">pt</p>
                </div>
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Input
                    type="number"
                    min="10"
                    max="24"
                    value={settings.font_sizes?.subtitle || 14}
                    onChange={(e) => updateFontSize('subtitle', e.target.value)}
                    data-testid="pdf-font-subtitle"
                  />
                  <p className="text-xs text-muted-foreground">pt</p>
                </div>
                <div className="space-y-2">
                  <Label>Heading</Label>
                  <Input
                    type="number"
                    min="10"
                    max="18"
                    value={settings.font_sizes?.heading || 12}
                    onChange={(e) => updateFontSize('heading', e.target.value)}
                    data-testid="pdf-font-heading"
                  />
                  <p className="text-xs text-muted-foreground">pt</p>
                </div>
                <div className="space-y-2">
                  <Label>Body</Label>
                  <Input
                    type="number"
                    min="8"
                    max="14"
                    value={settings.font_sizes?.body || 10}
                    onChange={(e) => updateFontSize('body', e.target.value)}
                    data-testid="pdf-font-body"
                  />
                  <p className="text-xs text-muted-foreground">pt</p>
                </div>
                <div className="space-y-2">
                  <Label>Small</Label>
                  <Input
                    type="number"
                    min="6"
                    max="10"
                    value={settings.font_sizes?.small || 8}
                    onChange={(e) => updateFontSize('small', e.target.value)}
                    data-testid="pdf-font-small"
                  />
                  <p className="text-xs text-muted-foreground">pt</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Options Tab */}
        <TabsContent value="options" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Header & Footer Options</CardTitle>
              <CardDescription>
                Configure what information appears in the header and footer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Include Page Numbers</Label>
                  <p className="text-sm text-muted-foreground">Show page numbers in the footer</p>
                </div>
                <Switch
                  checked={settings.include_page_numbers}
                  onCheckedChange={(v) => updateSettings('include_page_numbers', v)}
                  data-testid="pdf-page-numbers"
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Include Generation Timestamp</Label>
                  <p className="text-sm text-muted-foreground">Show when the report was generated</p>
                </div>
                <Switch
                  checked={settings.include_generation_timestamp}
                  onCheckedChange={(v) => updateSettings('include_generation_timestamp', v)}
                  data-testid="pdf-timestamp"
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Include Generated By</Label>
                  <p className="text-sm text-muted-foreground">Show the name of the user who generated the report</p>
                </div>
                <Switch
                  checked={settings.include_generated_by}
                  onCheckedChange={(v) => updateSettings('include_generated_by', v)}
                  data-testid="pdf-generated-by"
                />
              </div>
              
              <div className="space-y-2 pt-4">
                <Label>Footer Text</Label>
                <Input
                  value={settings.footer_text}
                  onChange={(e) => updateSettings('footer_text', e.target.value)}
                  placeholder="Confidential - For Internal Use Only"
                  data-testid="pdf-footer-text"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Advanced Options</CardTitle>
              <CardDescription>
                Additional PDF generation options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Enable Logo</Label>
                  <p className="text-sm text-muted-foreground">Include organization logo in reports</p>
                </div>
                <Switch
                  checked={settings.logo_enabled}
                  onCheckedChange={(v) => updateSettings('logo_enabled', v)}
                  data-testid="pdf-logo-enabled"
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Cover Page</Label>
                  <p className="text-sm text-muted-foreground">Add a cover page to reports</p>
                </div>
                <Switch
                  checked={settings.cover_page_enabled}
                  onCheckedChange={(v) => updateSettings('cover_page_enabled', v)}
                  data-testid="pdf-cover-page"
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Confidential Watermark</Label>
                  <p className="text-sm text-muted-foreground">Add a Confidential watermark to all pages</p>
                </div>
                <Switch
                  checked={settings.confidential_watermark}
                  onCheckedChange={(v) => updateSettings('confidential_watermark', v)}
                  data-testid="pdf-watermark"
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div className="space-y-0.5">
                  <Label>Signature Lines</Label>
                  <p className="text-sm text-muted-foreground">Include signature lines at the end of reports</p>
                </div>
                <Switch
                  checked={settings.signature_lines_enabled}
                  onCheckedChange={(v) => updateSettings('signature_lines_enabled', v)}
                  data-testid="pdf-signature-lines"
                />
              </div>
              
              <div className="space-y-2 pt-4">
                <Label>Disclaimer Text</Label>
                <Textarea
                  value={settings.disclaimer_text}
                  onChange={(e) => updateSettings('disclaimer_text', e.target.value)}
                  placeholder="Optional disclaimer text to appear at the end of reports"
                  rows={3}
                  data-testid="pdf-disclaimer"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PDFSettingsPage;
